import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';

const COOKIE_CONSENT_KEY = 'admin-hv-cookie-consent';

// Cookie categories
const COOKIE_CATEGORIES = {
  necessary: {
    id: 'necessary',
    name: 'Notwendige Cookies',
    description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.',
    required: true,
    defaultEnabled: true
  },
  functional: {
    id: 'functional',
    name: 'Funktionale Cookies',
    description: 'Diese Cookies ermöglichen erweiterte Funktionen wie die Einbettung von Google Maps.',
    required: false,
    defaultEnabled: false
  },
  analytics: {
    id: 'analytics',
    name: 'Analyse Cookies',
    description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren.',
    required: false,
    defaultEnabled: false
  }
};

export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    return consent ? JSON.parse(consent) : null;
  } catch {
    return null;
  }
};

export const hasConsent = (category) => {
  const consent = getCookieConsent();
  if (!consent) return false;
  return consent.categories[category] === true;
};

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false
  });

  useEffect(() => {
    // Check if consent was already given
    const existingConsent = getCookieConsent();
    if (!existingConsent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (categories) => {
    const consent = {
      timestamp: new Date().toISOString(),
      categories: categories
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);
    
    // Reload page to apply cookie settings
    window.location.reload();
  };

  const handleAcceptAll = () => {
    saveConsent({
      necessary: true,
      functional: true,
      analytics: true
    });
  };

  const handleRejectAll = () => {
    saveConsent({
      necessary: true,
      functional: false,
      analytics: false
    });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (category) => {
    if (COOKIE_CATEGORIES[category].required) return;
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" />
      
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center">
                <Cookie className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-bold text-lg">Cookie-Einstellungen</h2>
                <p className="text-sm text-slate-300">Datenschutz ist uns wichtig</p>
              </div>
            </div>
            <Shield className="w-8 h-8 text-teal-400" />
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-slate-600 mb-6">
              Wir verwenden Cookies und ähnliche Technologien, um Ihnen ein optimales 
              Website-Erlebnis zu bieten. Einige Cookies sind für den Betrieb der Website 
              notwendig, andere helfen uns, die Website zu verbessern. Sie können selbst 
              entscheiden, welche Cookies Sie zulassen möchten.
            </p>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Button
                onClick={handleAcceptAll}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 rounded-full"
              >
                Alle akzeptieren
              </Button>
              <Button
                onClick={handleRejectAll}
                variant="outline"
                className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold py-3 rounded-full"
              >
                Nur notwendige
              </Button>
            </div>

            {/* Details Toggle */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium mb-4 transition-colors"
            >
              {showDetails ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Details ausblenden
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Details anzeigen
                </>
              )}
            </button>

            {/* Cookie Categories */}
            {showDetails && (
              <div className="space-y-4 mb-6 border-t pt-6">
                {Object.values(COOKIE_CATEGORIES).map((category) => (
                  <div
                    key={category.id}
                    className="flex items-start justify-between p-4 bg-slate-50 rounded-xl"
                  >
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900">
                          {category.name}
                        </h3>
                        {category.required && (
                          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                            Erforderlich
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">
                        {category.description}
                      </p>
                    </div>
                    <Switch
                      checked={preferences[category.id]}
                      onCheckedChange={() => togglePreference(category.id)}
                      disabled={category.required}
                      className="data-[state=checked]:bg-teal-600"
                    />
                  </div>
                ))}

                <Button
                  onClick={handleSavePreferences}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-full"
                >
                  Auswahl speichern
                </Button>
              </div>
            )}

            {/* Footer Links */}
            <div className="flex flex-wrap gap-4 text-sm text-slate-500 pt-4 border-t">
              <a href="#datenschutz" className="hover:text-teal-600 transition-colors">
                Datenschutzerklärung
              </a>
              <a href="#impressum" className="hover:text-teal-600 transition-colors">
                Impressum
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
